import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

const COLLAPSED_COLUMN_WIDTH = 36

interface UseColumnResizeOptions<Key extends string> {
    visibleKeys: Key[]
    getWidth: (key: Key) => number
    getMinWidth?: (key: Key) => number
    isCollapsed: (key: Key) => boolean
    resizeColumn: (key: Key, width: number) => void
    fixedColumnsWidth: number
}

export const useColumnResize = <Key extends string>({
    visibleKeys,
    getWidth,
    getMinWidth,
    isCollapsed,
    resizeColumn,
    fixedColumnsWidth,
}: UseColumnResizeOptions<Key>) => {
    const scrollContainerRef = useRef<HTMLDivElement | null>(null)
    const resizingRef = useRef<{ key: Key; startX: number; startWidth: number } | null>(null)
    const cleanupRef = useRef<(() => void) | null>(null)
    const [containerWidth, setContainerWidth] = useState(0)
    const [activeResizeKey, setActiveResizeKey] = useState<Key | null>(null)

    useLayoutEffect(() => {
        const element = scrollContainerRef.current
        if (!element) return
        const update = () => setContainerWidth(element.clientWidth)
        update()
        const observer = new ResizeObserver(update)
        observer.observe(element)
        return () => observer.disconnect()
    }, [])

    useEffect(
        () => () => {
            cleanupRef.current?.()
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
        },
        []
    )

    const effectiveWidth = useMemo<Record<string, number>>(() => {
        const widths: Record<string, number> = {}
        visibleKeys.forEach((key) => {
            widths[key] = isCollapsed(key) ? COLLAPSED_COLUMN_WIDTH : getWidth(key)
        })
        if (!containerWidth || visibleKeys.length === 0) return widths

        const expandedKeys = visibleKeys.filter((key) => !isCollapsed(key))
        if (expandedKeys.length === 0) return widths
        const collapsedTotal = (visibleKeys.length - expandedKeys.length) * COLLAPSED_COLUMN_WIDTH
        const baseExpanded = expandedKeys.reduce((sum, key) => sum + getWidth(key), 0)
        const available = Math.max(0, containerWidth - fixedColumnsWidth - collapsedTotal)
        if (baseExpanded <= 0 || baseExpanded >= available) return widths

        const scale = available / baseExpanded
        expandedKeys.forEach((key) => {
            const scaled = getWidth(key) * scale
            widths[key] = getMinWidth ? Math.max(getMinWidth(key), scaled) : scaled
        })
        return widths
    }, [containerWidth, fixedColumnsWidth, getMinWidth, getWidth, isCollapsed, visibleKeys])

    const handleResizeStart = (key: Key) => (event: React.MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
        cleanupRef.current?.()
        resizingRef.current = { key, startX: event.clientX, startWidth: getWidth(key) }
        setActiveResizeKey(key)
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'

        const handleMove = (moveEvent: MouseEvent) => {
            const context = resizingRef.current
            if (context) resizeColumn(context.key, context.startWidth + moveEvent.clientX - context.startX)
        }
        const cleanup = () => {
            document.removeEventListener('mousemove', handleMove)
            document.removeEventListener('mouseup', cleanup)
            resizingRef.current = null
            cleanupRef.current = null
            setActiveResizeKey(null)
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
        }
        cleanupRef.current = cleanup
        document.addEventListener('mousemove', handleMove)
        document.addEventListener('mouseup', cleanup)
    }

    return { scrollContainerRef, effectiveWidth, handleResizeStart, activeResizeKey }
}
